import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, HostListener, inject, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { GenericTemplateCommonService } from '../template-ai-common/services/generic-template-common.service';
import { MatDialog } from '@angular/material/dialog';
import { AddCompetitorDialogComponent } from '../../../dialogs/add-competitor-dialog/add-competitor-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonConfirmDialogComponent } from '../../../dialogs/common-confirm-dialog/common-confirm-dialog.component';
import { firstValueFrom } from 'rxjs';
import { Project, ProjectCreateRequest, ProjectManagementService } from '../../../services/project-management.service';

export interface SelectorItem {
  id: string;
  name: string;
  type: 'primary' | 'competitor';
  color: string;
  domain?: string;
  removable?: boolean;
}

@Component({
  selector: 'app-horizontal-selector-bar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './horizontal-selector-bar.component.html',
  styleUrl: './horizontal-selector-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorizontalSelectorBarComponent implements OnInit {
  @Input() entityType: 'university' | 'domain' | 'brand' = 'university';
  @Input() maxCompetitors: number = 3; // Changed default to 3
  @Input() availableOptions: string[] = [];
  @Input() projectId: string | null = null;
  
  // Internal state for selected items  
  _internalSelectedItems: SelectorItem[] = [];
  
  @Input()
  set selectedItems(value: SelectorItem[]) {
    // Initialize from parent only if we have no items yet, OR allow internal updates
    if (value && value.length > 0) {
      // If this is coming from parent (external) and we already have items with empty slots, ignore it
      // This prevents parent from overwriting our state when we add empty competitors
      const hasEmptySlots = this._internalSelectedItems.some(item => !item.name && item.type === 'competitor');
      if (hasEmptySlots && value.length < this._internalSelectedItems.length) {
        return;
      }
      this._internalSelectedItems = value;
      this.changeDetectorRef.markForCheck();
    } else if (value && value.length === 0 && this._internalSelectedItems.length === 0) {
      // Allow setting empty array initially
      this._internalSelectedItems = [];
    }
  }
  get selectedItems(): SelectorItem[] {
    return this._internalSelectedItems;
  }

  @Output() selectionChange = new EventEmitter<SelectorItem[]>();
  @Output() compareClick = new EventEmitter<SelectorItem[]>();

  // Services
  private router = inject(Router);
  private authService = inject(AuthService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private projectService = inject(ProjectManagementService);

  // Component state
  newCompetitorInput: string = '';
  filteredOptions: string[] = [];
  showAddField: boolean = false;
  activeInputIndex: number = -1; // Track which item is showing input

  // Primary selection state
  showPrimaryDropdown: boolean = false;
  primarySearchInput: string = '';
  filteredPrimaryOptions: string[] = [];

  // Color palette for competitor indicators
  private colorPalette = [
    '#4CAF50', // teal/mint green
    '#FF9800', // orange
    '#9C27B0', // purple
    '#607D8B', // blue-grey
    '#E91E63', // pink
    '#2196F3', // blue
    '#FF5722', // deep orange
    '#795548', // brown
    '#607D8B', // blue-grey
    '#9E9E9E'  // grey
  ];

  constructor(private templateCommonService: GenericTemplateCommonService) {
  }

  ngOnInit(): void {
    this.initializeDefaultSelection();
    this.updateFilteredPrimaryOptions();
  }

  private initializeDefaultSelection(): void {
    if (this._internalSelectedItems.length === 0) {
      // Set default values for university selection
      if (this.entityType === 'university') {
        const defaultItems = [
          {
            id: 'primary',
            name: 'Warwick Business School (YOU)',
            type: 'primary' as const,
            color: '#2196F3',
            removable: false
          },
          {
            id: 'competitor-1',
            name: 'University of Oxford',
            type: 'competitor' as const,
            color: '#4CAF50',
            removable: true
          },
          {
            id: 'competitor-1',
            name: 'London Business School',
            type: 'competitor' as const,
            color: '#4CAF50',
            removable: true
          }
        ];
        this._internalSelectedItems = defaultItems;
        // Emit the default values to parent component
        this.selectionChange.emit(defaultItems);
      } else {
        const defaultItems = [
          {
            id: 'primary',
            name: '',
            type: 'primary' as const,
            color: '#2196F3',
            removable: false
          }
        ];
        this._internalSelectedItems = defaultItems;
        // Emit the default values to parent component
        this.selectionChange.emit(defaultItems);
      }
    }
  }

  onAddCompetitor(): void {
    if (this.selectedItems.length >= this.maxCompetitors + 1) return; // +1 for primary

    // If we don't have any competitors yet, add an empty one
    if (this.selectedItems.length === 1) {
      const competitorIndex = this.selectedItems.length;
      const color = this.colorPalette[(competitorIndex - 1) % this.colorPalette.length];

      const emptyCompetitor: SelectorItem = {
        id: `competitor-${Date.now()}`,
        name: '', // Empty name to show "Add competitor"
        type: 'competitor',
        color: color,
        removable: true
      };

      this.selectedItems.push(emptyCompetitor);
    }

    this.showAddField = true;
    this.activeInputIndex = this.selectedItems.length - 1; // Show input for the last item
    this.filteredOptions = this.availableOptions.filter(option =>
      !this.selectedItems.some(item => item.name === option)
    );
  }

  onAddMoreCompetitors(): void {
    const remainingSlots = this.maxCompetitors + 1 - this.selectedItems.length;
    const slotsToAdd = Math.min(3, remainingSlots);

    for (let i = 0; i < slotsToAdd; i++) {
      const competitorIndex = this.selectedItems.length;
      const color = this.colorPalette[(competitorIndex - 1) % this.colorPalette.length];

      const emptyCompetitor: SelectorItem = {
        id: `competitor-${Date.now()}-${i}`,
        name: '', // Empty name to show "Add competitor"
        type: 'competitor',
        color: color,
        removable: true
      };

      this.selectedItems.push(emptyCompetitor);
    }
  }

  // Add a single competitor slot
  onAddSingleCompetitor(): void {
    if (this.selectedItems.length >= this.maxCompetitors + 1) return;

    const competitorIndex = this.selectedItems.length;
    const color = this.colorPalette[(competitorIndex - 1) % this.colorPalette.length];

    const emptyCompetitor: SelectorItem = {
      id: `competitor-${Date.now()}`,
      name: '', // Empty name to show "Add competitor"
      type: 'competitor',
      color: color,
      removable: true
    };

    // this.selectedItems.push(emptyCompetitor);
    this.selectedItems = [...this.selectedItems, emptyCompetitor];
    
    // Show input field immediately for the newly added empty competitor
    this.showAddField = true;
    this.activeInputIndex = this.selectedItems.length - 1;
    this.filteredOptions = this.availableOptions.filter(option =>
      !this.selectedItems.some(item => item.name === option)
    );
    
    // DON'T emit when adding empty slots - only emit when actual competitor is selected
    // This prevents circular update loop with parent
    // this.selectionChange.emit([...this.selectedItems]);
    
    // Force change detection to update the view
    this.changeDetectorRef.detectChanges();
  }

  // Check if we should show "Add competitor" placeholder
  shouldShowAddCompetitor(index: number): boolean {
    return index === this.selectedItems.length - 1 &&
      this.selectedItems.length > 1 &&
      !this.selectedItems[index].name;
  }

  onCompetitorInputChange(value: string): void {
    this.newCompetitorInput = value;
    this.filteredOptions = this.availableOptions.filter(option => {
      // Check if option matches search value
      if (!option.toLowerCase().includes(value.toLowerCase())) {
        return false;
      }

      // Check if exact name already exists
      if (this.selectedItems.some(item => item.name === option)) {
        return false;
      }

      // Check if domain/site already exists
      const optionDomain = this.extractDomainFromName(option);
      if (optionDomain) {
        const domainAlreadyExists = this.selectedItems.some(item => {
          if (!item.name) return false;
          const itemDomain = this.extractDomainFromName(item.name);
          return itemDomain && itemDomain.toLowerCase() === optionDomain.toLowerCase();
        });
        if (domainAlreadyExists) {
          return false;
        }
      }

      return true;
    });
  }

  onCompetitorSelected(option: string): void {
    if (!option) return;

    // Check if exact name already exists
    if (this.selectedItems.some(item => item.name === option)) {
      return;
    }

    // Extract domain/site from option for comparison
    const optionDomain = this.extractDomainFromName(option);
    
    // Check if domain/site already exists in any selected item
    const domainAlreadyExists = this.selectedItems.some(item => {
      if (!item.name) return false;
      const itemDomain = this.extractDomainFromName(item.name);
      return itemDomain && optionDomain && itemDomain.toLowerCase() === optionDomain.toLowerCase();
    });

    if (domainAlreadyExists) {
      return; // Don't add if domain already exists
    }

    // Find the first empty competitor slot
    const emptyIndex = this.selectedItems.findIndex(item => item.type === 'competitor' && !item.name);

    if (emptyIndex !== -1) {
      // Replace the empty competitor with the selected option
      this.selectedItems[emptyIndex] = {
        ...this.selectedItems[emptyIndex],
        name: option
      };
    } else {
      // If no empty slot, add a new competitor
      const competitorIndex = this.selectedItems.length - 1;
      const color = this.colorPalette[competitorIndex % this.colorPalette.length];

      const newCompetitor: SelectorItem = {
        id: `competitor-${Date.now()}`,
        name: option,
        type: 'competitor',
        color: color,
        removable: true
      };

      this.selectedItems.push(newCompetitor);
    }

    this.newCompetitorInput = '';
    this.showAddField = false;
    this.activeInputIndex = -1;
    
    // Emit when actual competitor is selected (has a name)
    this.selectionChange.emit([...this.selectedItems]);
  }

  /**
   * Extract domain/site from a name string
   * Handles formats like:
   * - "www.mg.co.uk"
   * - "MG - www.mg.co.uk (YOU)"
   * - "Brand - www.example.com"
   * Returns the domain part or null if no domain found
   */
  private extractDomainFromName(name: string): string | null {
    if (!name) return null;

    // Remove "(YOU)" suffix if present
    let cleanName = name.replace(/\s*\(YOU\)\s*$/, '').trim();

    // Try to extract domain patterns
    // Pattern 1: "Brand - www.domain.com" or "Brand - domain.com"
    const dashPattern = /-\s*(www\.)?([^\s-]+\.(com|co\.uk|org|net|io|edu|gov|uk|us|ca|au|de|fr|es|it|jp|cn|in|br|mx|nl|sg|ae|sa))/i;
    const dashMatch = cleanName.match(dashPattern);
    if (dashMatch) {
      return dashMatch[2].toLowerCase();
    }

    // Pattern 2: Direct domain like "www.domain.com" or "domain.com"
    const directDomainPattern = /^(www\.)?([^\s-]+\.(com|co\.uk|org|net|io|edu|gov|uk|us|ca|au|de|fr|es|it|jp|cn|in|br|mx|nl|sg|ae|sa))$/i;
    const directMatch = cleanName.match(directDomainPattern);
    if (directMatch) {
      return directMatch[2].toLowerCase();
    }

    // Pattern 3: Domain anywhere in the string
    const anywhereDomainPattern = /(www\.)?([a-z0-9-]+\.(com|co\.uk|org|net|io|edu|gov|uk|us|ca|au|de|fr|es|it|jp|cn|in|br|mx|nl|sg|ae|sa))/i;
    const anywhereMatch = cleanName.match(anywhereDomainPattern);
    if (anywhereMatch) {
      return anywhereMatch[2].toLowerCase();
    }

    // If no domain found, return null
    return null;
  }

  async onRemoveItem(item: SelectorItem, index: number): Promise<void> {
    if (index <= 0) return;
    if (this.entityType !== 'brand' || !this.projectId) {
      this.selectedItems.splice(index, 1);
      this.selectionChange.emit([...this.selectedItems]);
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open(CommonConfirmDialogComponent, {
          width: '460px',
          disableClose: true,
          data: {
            title: 'Confirm Competitor Removal',
            description:
              `This will remove competitor "${item.name}", delete old analysis versions, and start a fresh Re-Analysis. Do you want to continue?`,
          },
        })
        .afterClosed(),
    );
    if (!confirmed) return;

    try {
      const project = await firstValueFrom(this.projectService.getProjectById(this.projectId));
      const request = this.buildUpdateRequestAfterCompetitorRemoval(project, item);
      await firstValueFrom(this.projectService.updateProject(this.projectId, request));
      await firstValueFrom(this.projectService.deleteProjectAnalysisVersions(this.projectId));

      this.templateCommonService.initializeGlobalLoaderForBulkAnalysis();
      this.snackBar.open('Competitor removed and Re-Analysis started.', 'Close', {
        duration: 3000,
      });
      window.location.href = `/common-ai-analysis/generic/${this.projectId}`;
    } catch (err: any) {
      this.snackBar.open(err?.error?.detail || 'Failed to remove competitor', 'Close', {
        duration: 5000,
      });
    }
  }

  onRemoveCompetitor(item: SelectorItem): void {
    if (item.removable) {
      this.selectedItems = this.selectedItems.filter(i => i.id !== item.id);
      this.selectionChange.emit([...this.selectedItems]);
    }
  }



  onPrimaryChipClick(): void {
    return;
    // this.showPrimaryDropdown = !this.showPrimaryDropdown;
    // this.activeInputIndex = this.showPrimaryDropdown ? 0 : -1;
    // if (this.showPrimaryDropdown) {
    //   this.updateFilteredPrimaryOptions();
    // }
  }

  onPrimarySearchChange(value: string): void {
    this.primarySearchInput = value;
    this.updateFilteredPrimaryOptions();
  }

  onPrimaryOptionSelected(option: string): void {
    if (option) {
      this.selectedItems[0] = {
        ...this.selectedItems[0],
        name: option
      };
      this.showPrimaryDropdown = false;
      this.activeInputIndex = -1;
      this.primarySearchInput = '';
      this.selectionChange.emit([...this.selectedItems]);
    }
  }

  private updateFilteredPrimaryOptions(): void {
    this.filteredPrimaryOptions = this.availableOptions.filter(option => {
      // Check if option matches search value
      if (!option.toLowerCase().includes(this.primarySearchInput.toLowerCase())) {
        return false;
      }

      // Check if exact name already exists
      if (this.selectedItems.some(item => item.name === option)) {
        return false;
      }

      // Check if domain/site already exists
      const optionDomain = this.extractDomainFromName(option);
      if (optionDomain) {
        const domainAlreadyExists = this.selectedItems.some(item => {
          if (!item.name) return false;
          const itemDomain = this.extractDomainFromName(item.name);
          return itemDomain && itemDomain.toLowerCase() === optionDomain.toLowerCase();
        });
        if (domainAlreadyExists) {
          return false;
        }
      }

      return true;
    });
  }

  onPrimarySelectionChange(value: string): void {
    if (value) {
      this.selectedItems[0] = {
        ...this.selectedItems[0],
        name: value
      };
      this.selectionChange.emit([...this.selectedItems]);
    }
  }

  onCompare(): void {
    if (this.selectedItems.length > 0) {
      this.compareClick.emit([...this.selectedItems]);
    }
  }

  canAddCompetitor(): boolean {
    return this.selectedItems.length < this.maxCompetitors + 1;
  }

  canCompare(): boolean {
    return this.selectedItems.length > 1 &&
      this.selectedItems.every(item => item.name &&
        item.name !== 'Select your university' &&
        item.name !== 'Select your domain' &&
        item.name !== 'Select your brand' &&
        item.name?.trim() !== '');
  }

  /** Domain of the primary (first) brand for tooltip display, e.g. "mg.co.uk". Prefers item.domain from project. */
  get primaryBrandDomain(): string {
    const primary = this.selectedItems[0];
    if (!primary) return '';
    if (primary.domain) return primary.domain;
    if (!primary.name) return '';
    const domain = this.extractDomainFromName(primary.name);
    return domain || '';
  }

  /** Tooltip for each chip: show brand/competitor domain (from project API url) when available, else fallback. */
  getTooltipForItem(item: SelectorItem, index: number): string {
    // Prefer domain from API (competitors.url / brand description)
    if (item?.domain && item.domain.trim()) return item.domain.trim();
    // Primary brand: try domain from name pattern (e.g. "Brand - www.example.com")
    if (index === 0) return this.primaryBrandDomain || 'You can change this label from your project page';
    // Competitor: try to show domain parsed from name as fallback
    if (item?.name) {
      const fromName = this.extractDomainFromName(item.name);
      if (fromName) return fromName;
    }
    return 'You can change this by clicking and selecting from dropdown or by typing a custom name and pressing enter';
  }

  getPrimaryOptions(): string[] {
    return this.availableOptions.filter(option => {
      // Check if exact name already exists
      if (this.selectedItems.some(item => item.name === option)) {
        return false;
      }

      // Check if domain/site already exists
      const optionDomain = this.extractDomainFromName(option);
      if (optionDomain) {
        const domainAlreadyExists = this.selectedItems.some(item => {
          if (!item.name) return false;
          const itemDomain = this.extractDomainFromName(item.name);
          return itemDomain && itemDomain.toLowerCase() === optionDomain.toLowerCase();
        });
        if (domainAlreadyExists) {
          return false;
        }
      }

      return true;
    });
  }

  openAddCompetitorDialog(): void {
    if (this.entityType !== 'brand') return;
    if (!this.projectId) {
      this.snackBar.open('Project context not found. Please reopen this project.', 'Close', {
        duration: 4000,
      });
      return;
    }

    const rawPrimary = (this.selectedItems[0]?.name || '').trim();
    const projectName = rawPrimary.replace(/\s*\(YOU\)\s*$/i, '').trim() || 'Project';

    this.dialog.open(AddCompetitorDialogComponent, {
      width: '80%',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      data: { projectName, projectId: this.projectId, primaryDomain: this.primaryBrandDomain },
    });
  }

  navigateToProjectPage(): void {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && currentUser.role === 'admin') {
      this.router.navigate(['/admin/projects']);
    } else {
      this.router.navigate(['/my-projects']);
    }
  }

  private buildUpdateRequestAfterCompetitorRemoval(project: Project, selectedItem: SelectorItem): ProjectCreateRequest {
    const removedName = (selectedItem.name || '').trim().toLowerCase();
    const removedDomain = this.normalizeDomain(selectedItem.domain || this.extractDomainFromName(selectedItem.name || '') || '');
    const nextCompetitors = (project.competitors || []).filter((c) => {
      const name = (c?.name || '').trim().toLowerCase();
      const domain = this.normalizeDomain(c?.url || '');
      const nameMatch = !!removedName && name === removedName;
      const domainMatch = !!removedDomain && !!domain && domain === removedDomain;
      return !(nameMatch || domainMatch);
    });

    return {
      name: project.name,
      description: project.description || '',
      primary_country: project.primary_country || 'Global',
      language: project.language || 'English',
      competitors: nextCompetitors,
      industry: project.industry,
      category: project.category,
      industries: project.industries,
      analysis_categories: project.analysis_categories,
    };
  }

  private normalizeDomain(value: string): string {
    const raw = (value || '').trim().toLowerCase();
    if (!raw) return '';
    try {
      const parsed = new URL(/^https?:\/\//.test(raw) ? raw : `https://${raw}`);
      return parsed.hostname.replace(/^www\./, '');
    } catch {
      return raw.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0].split(':')[0];
    }
  }
}
